// BeaconTree.tsx — live "tree" of every sheet beacon.
//
//   (hub) Beacon Hub
//     │
//     ├──●── Diwali Meta Leads      ● ● ● ● ●  next in 4:12
//     ├──●── Excel Batch Form       ● ● ● ✕ ●  failing
//     └──●── Walk-in Register       paused
//
// Every leaf shows: a blinking health dot, its heartbeat (last 12 checks),
// a countdown to the next check, new-lead alerts and the beacon's actions.
// Dots flow along a branch toward the hub while that sheet is being checked
// or has new leads waiting.

import React from 'react';
import {
  Check,
  ExternalLink,
  History,
  Pause,
  Pencil,
  Play,
  Plus,
  Radar,
  RefreshCw,
  Send,
  Trash2,
  TriangleAlert,
  Zap,
} from 'lucide-react';
import { C } from '../../../components/GlobalComponents/uiTokens';
import { SheetTile, shortId, fmtDateTime } from '../../SheetsManager/parts/SheetImportDetailsModal';
import BeaconDot, { useBeaconCss } from './BeaconDot';
import {
  BeaconSummary,
  HEALTH,
  Health,
  PingLite,
  SheetBeacon,
  beaconName,
  cycleProgress,
  fmtAgo,
  fmtCountdown,
  healthOf,
  intervalShort,
  RECENT_PINGS_KEPT as RecentPingsKept,
} from './beaconTypes';

const TREE_CSS = `
.bct-leaf { position: relative; padding: 6px 0 6px 54px; }
.bct-trunk { position: absolute; left: 22px; top: 0; bottom: 0; width: 2px; background: rgba(255,255,255,0.14); }
.bct-leaf.bct-last .bct-trunk { bottom: 50%; }
.bct-branch { position: absolute; left: 22px; top: 50%; width: 32px; height: 2px; margin-top: -1px; overflow: visible; }
.bct-knot { position: absolute; left: 18px; top: 50%; width: 10px; height: 10px; margin-top: -5px; border-radius: 50%; border: 2px solid #0f4a46; }
.bct-flow { position: absolute; top: 50%; left: 0; width: 6px; height: 6px; margin-top: -3px; border-radius: 50%; opacity: 0; }
.bct-run .bct-flow { animation: bctFlow 1.2s linear infinite; }
.bct-run .bct-flow.b { animation-delay: -0.4s; }
.bct-run .bct-flow.c { animation-delay: -0.8s; }
.bct-beat .bct-flow.a { animation: bctFlow 2.6s ease-in-out infinite; }
@keyframes bctFlow { 0% { transform: translateX(28px); opacity: 0; } 15% { opacity: 1; } 85% { opacity: 1; } 100% { transform: translateX(0); opacity: 0; } }
.bct-card { transition: border-color .25s, box-shadow .25s, background .25s; }
.bct-card:hover { background: rgba(255,255,255,0.085) !important; }
.bct-icon:hover:not(:disabled) { background: rgba(255,255,255,0.16) !important; color: #fff !important; }
.bct-icon.danger:hover:not(:disabled) { background: rgba(239,68,68,0.22) !important; color: #fecaca !important; }
.bct-lime:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(200,233,75,0.5) !important; }
.bct-spin { animation: bctSpin 0.9s linear infinite; }
@keyframes bctSpin { to { transform: rotate(360deg); } }
.bct-hub-ring { position: absolute; inset: -5px; border-radius: 16px; border: 2px solid rgba(200,233,75,0.55); animation: bctHub 2.4s ease-out infinite; }
@keyframes bctHub { 0% { transform: scale(0.92); opacity: 0.9; } 100% { transform: scale(1.18); opacity: 0; } }
@media (prefers-reduced-motion: reduce) { .bct-flow, .bct-hub-ring, .bct-spin { animation: none !important; } }
@media (max-width: 520px) { .bct-pings { display: none; } }
`;

interface BeaconTreeProps {
  beacons: SheetBeacon[];
  summary: BeaconSummary | null;
  /** Current time in ms, already corrected for server clock skew. */
  now: number;
  loading?: boolean;
  /** Beacon ids with a request in flight (ping / pause / seen). */
  busyIds: Set<string>;
  onAdd: () => void;
  onPing: (b: SheetBeacon) => void;
  onToggle: (b: SheetBeacon) => void;
  onEdit: (b: SheetBeacon) => void;
  onDelete: (b: SheetBeacon) => void;
  onSeen: (b: SheetBeacon) => void;
  onHistory: (b: SheetBeacon) => void;
  /** Optional: jump to Sheet Imports to pull the new rows in. */
  onSync?: (b: SheetBeacon) => void;
}

// ── Small pieces ──────────────────────────────────────────────────────────────

const Chip: React.FC<{ health: Health; children: React.ReactNode }> = ({ health, children }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '3.7px 10px',
      borderRadius: '20px',
      background: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.16)',
      fontSize: '0.63rem',
      fontWeight: 700,
      color: 'rgba(255,255,255,0.92)',
      whiteSpace: 'nowrap',
    }}
  >
    <BeaconDot health={health} size={7} />
    {children}
  </span>
);

/** Last N checks as a row of dots, oldest → newest. */
export const Heartbeat: React.FC<{ pings: PingLite[] | null; dark?: boolean }> = ({ pings, dark = true }) => {
  const list = (pings ?? []).slice(-RecentPingsKept);
  const empty = RecentPingsKept - list.length;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} aria-label="Recent checks">
      {Array.from({ length: empty }).map((_, i) => (
        <span key={`e${i}`} style={{ width: 7, height: 7, borderRadius: '50%', background: dark ? 'rgba(255,255,255,0.12)' : '#e5e7eb' }} />
      ))}
      {list.map((p, i) => {
        const isNew = p.status === 'ok' && p.new_rows > 0;
        const color = p.status === 'error' ? '#ef4444' : isNew ? '#c8e94b' : '#22c55e';
        const size = isNew ? 10 : 7;
        return (
          <span
            key={`${p.checked_at}-${i}`}
            title={`${fmtDateTime(p.checked_at)} · ${p.status === 'error' ? 'failed' : isNew ? `+${p.new_rows} new` : 'no change'}`}
            style={{
              width: size,
              height: size,
              borderRadius: '50%',
              background: color,
              opacity: i === list.length - 1 ? 1 : 0.55 + (0.45 * i) / Math.max(1, list.length - 1),
              boxShadow: isNew || i === list.length - 1 ? `0 0 8px ${color}` : 'none',
            }}
          />
        );
      })}
    </div>
  );
};

const IconBtn: React.FC<{
  title: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ title, onClick, disabled, danger, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    disabled={disabled}
    className={`bct-icon${danger ? ' danger' : ''}`}
    style={{
      width: 28,
      height: 28,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '7px',
      border: '1px solid rgba(255,255,255,0.14)',
      background: 'rgba(255,255,255,0.06)',
      color: danger ? '#fca5a5' : 'rgba(255,255,255,0.8)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      transition: 'all 0.2s',
      padding: 0,
    }}
  >
    {children}
  </button>
);

// ── One leaf ──────────────────────────────────────────────────────────────────

const Leaf: React.FC<{
  b: SheetBeacon;
  last: boolean;
  now: number;
  busy: boolean;
  props: BeaconTreeProps;
}> = ({ b, last, now, busy, props }) => {
  const health: Health = healthOf(b);
  const h = HEALTH[health];
  const checking = health === 'checking';
  const flowing = checking || health === 'new';
  const progress = cycleProgress(b, now);

  const branchBg =
    health === 'paused'
      ? 'repeating-linear-gradient(90deg, rgba(255,255,255,0.25) 0 4px, transparent 4px 8px)'
      : `linear-gradient(90deg, rgba(255,255,255,0.14), ${h.color})`;

  const cardBorder =
    health === 'new' ? 'rgba(200,233,75,0.65)' : health === 'error' ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.12)';

  return (
    <div className={`bct-leaf${last ? ' bct-last' : ''}`}>
      <span className="bct-trunk" />
      <span className={`bct-branch ${flowing ? 'bct-run' : health === 'live' ? 'bct-beat' : ''}`} style={{ background: branchBg }}>
        <span className="bct-flow a" style={{ background: h.color, boxShadow: `0 0 8px ${h.glow}` }} />
        <span className="bct-flow b" style={{ background: h.color, boxShadow: `0 0 8px ${h.glow}` }} />
        <span className="bct-flow c" style={{ background: h.color, boxShadow: `0 0 8px ${h.glow}` }} />
      </span>
      <span className="bct-knot" style={{ background: h.color }} />

      <div
        className="bct-card"
        style={{
          borderRadius: '12px',
          padding: '11px 12px',
          background: 'rgba(255,255,255,0.06)',
          border: `1px solid ${cardBorder}`,
          boxShadow: health === 'new' ? '0 0 22px rgba(200,233,75,0.18)' : 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          opacity: health === 'paused' ? 0.72 : 1,
        }}
      >
        {/* Top row: identity · heartbeat · timing */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 220px', minWidth: 0 }}>
            <div style={{ position: 'relative' }}>
              <SheetTile size={34} />
              <span style={{ position: 'absolute', right: -3, top: -3, display: 'flex' }}>
                <BeaconDot health={health} size={11} />
              </span>
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                title={b.display_name || b.sheet_id}
                style={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {beaconName(b)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '2px', fontSize: '0.62rem', color: 'rgba(255,255,255,0.6)' }}>
                {b.display_name && <span style={{ fontFamily: 'monospace' }}>{shortId(b.sheet_id)}</span>}
                <span style={{ padding: '1px 7px', borderRadius: '10px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)' }}>
                  every {intervalShort(b.interval_minutes)}
                </span>
                {b.baseline_set && <span>{b.last_row_count} rows</span>}
                <span style={{ color: h.color, fontWeight: 700 }}>{h.label}</span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 1 auto' }}>
            <Heartbeat pings={b.recent_pings} />
            <span style={{ fontSize: '0.56rem', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.4px', textTransform: 'uppercase', fontWeight: 700 }}>
              Last {RecentPingsKept} checks
            </span>
          </div>

          <div style={{ flex: '0 0 128px', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end', marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#fff', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {checking ? (
                <span style={{ color: '#7dd3fc', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <RefreshCw size={12} className="bct-spin" /> Checking…
                </span>
              ) : health === 'paused' ? (
                <span style={{ color: 'rgba(255,255,255,0.6)' }}>Paused</span>
              ) : (
                <>
                  <span style={{ fontSize: '0.58rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', marginRight: '5px' }}>NEXT</span>
                  {fmtCountdown(b.next_check_at, now)}
                </>
              )}
            </span>
            <div style={{ width: '100%', height: '4px', borderRadius: '4px', background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${health === 'paused' ? 0 : checking ? 100 : progress * 100}%`,
                  height: '100%',
                  background: checking ? '#38bdf8' : `linear-gradient(90deg, rgba(200,233,75,0.4), ${C.lime})`,
                  transition: 'width 1s linear',
                }}
              />
            </div>
            <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
              checked {fmtAgo(b.last_checked_at, now)}
            </span>
          </div>
        </div>

        {/* New leads alert */}
        {b.unseen_new_rows > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              flexWrap: 'wrap',
              padding: '8px 10px',
              borderRadius: '9px',
              background: 'rgba(200,233,75,0.12)',
              border: '1px solid rgba(200,233,75,0.4)',
            }}
          >
            <Zap size={14} color={C.lime} />
            <span style={{ flex: '1 1 200px', fontSize: '0.72rem', color: '#ecfccb', fontWeight: 600 }}>
              <strong style={{ color: C.lime, fontSize: '0.82rem' }}>+{b.unseen_new_rows}</strong> new lead
              {b.unseen_new_rows === 1 ? '' : 's'} arrived · latest {fmtAgo(b.last_new_rows_at, now)}
            </span>
            {props.onSync && (
              <button
                type="button"
                onClick={() => props.onSync!(b)}
                className="bct-lime"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '5px 11px',
                  borderRadius: '7px',
                  border: 'none',
                  background: C.lime,
                  color: C.teal,
                  fontSize: '0.64rem',
                  fontWeight: 800,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <Send size={11} /> SYNC NOW
              </button>
            )}
            <button
              type="button"
              onClick={() => props.onSeen(b)}
              disabled={busy}
              className="bct-icon"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '5px 10px',
                borderRadius: '7px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.06)',
                color: 'rgba(255,255,255,0.85)',
                fontSize: '0.64rem',
                fontWeight: 700,
                cursor: busy ? 'wait' : 'pointer',
              }}
            >
              <Check size={11} /> MARK SEEN
            </button>
          </div>
        )}

        {/* Error */}
        {health === 'error' && b.last_error && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', fontSize: '0.68rem', color: '#fecaca', lineHeight: 1.5 }}>
            <TriangleAlert size={13} color="#f87171" style={{ flexShrink: 0, marginTop: '2px' }} />
            <span>
              {b.last_error}
              {b.consecutive_failures > 1 && <span style={{ color: 'rgba(254,202,202,0.7)' }}> · failed {b.consecutive_failures}× in a row</span>}
            </span>
          </div>
        )}
        {!b.is_active && b.last_error && b.consecutive_failures > 0 && (
          <div style={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.6)' }}>Paused: {b.last_error}</div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
          <IconBtn title="Ping now" onClick={() => props.onPing(b)} disabled={busy || checking}>
            <RefreshCw size={13} className={busy || checking ? 'bct-spin' : undefined} />
          </IconBtn>
          <IconBtn title={b.is_active ? 'Pause beacon' : 'Resume beacon'} onClick={() => props.onToggle(b)} disabled={busy}>
            {b.is_active ? <Pause size={13} /> : <Play size={13} />}
          </IconBtn>
          <IconBtn title="Ping history" onClick={() => props.onHistory(b)}>
            <History size={13} />
          </IconBtn>
          <IconBtn title="Rename / change interval" onClick={() => props.onEdit(b)}>
            <Pencil size={13} />
          </IconBtn>
          <IconBtn title="Open sheet" onClick={() => window.open(b.sheet_url, '_blank', 'noopener,noreferrer')}>
            <ExternalLink size={13} />
          </IconBtn>
          <span style={{ flex: 1 }} />
          <span className="bct-pings" style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)' }}>
            {b.total_pings} pings
          </span>
          <IconBtn title="Delete beacon" onClick={() => props.onDelete(b)} disabled={busy} danger>
            <Trash2 size={13} />
          </IconBtn>
        </div>
      </div>
    </div>
  );
};

// ── Tree ──────────────────────────────────────────────────────────────────────

const BeaconTree: React.FC<BeaconTreeProps> = (props) => {
  useBeaconCss();
  const { beacons, summary, now, loading, busyIds, onAdd } = props;

  const anyNew = (summary?.unseen_new_rows ?? 0) > 0;
  const live = beacons.filter((b) => healthOf(b) === 'live' || healthOf(b) === 'new' || healthOf(b) === 'checking').length;
  const failing = beacons.filter((b) => healthOf(b) === 'error').length;
  const paused = beacons.filter((b) => !b.is_active).length;
  const hubHealth: Health = beacons.length === 0 ? 'idle' : anyNew ? 'new' : failing > 0 ? 'error' : live > 0 ? 'live' : 'paused';

  return (
    <div style={{ background: '#f8f9f9', border: '1px solid #e8ebeb', borderRadius: '13px', padding: '4.6px' }}>
      <style>{TREE_CSS}</style>
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '9.2px',
          padding: '18px 18px 16px',
          background: 'linear-gradient(135deg, #0c403c 0%, #115550 50%, #16706a 100%)',
          color: '#fff',
        }}
      >
        {/* radar rings + glow */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(circle at 40px 40px, transparent 0 60px, rgba(255,255,255,0.05) 61px 62px, transparent 63px 120px, rgba(255,255,255,0.04) 121px 122px, transparent 123px)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 92% 10%, rgba(200,233,75,0.18), transparent 40%)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative' }}>
          {/* Hub */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', paddingBottom: '10px' }}>
            <div style={{ position: 'relative', width: 46, height: 46, flexShrink: 0 }}>
              {hubHealth !== 'paused' && <span className="bct-hub-ring" style={{ borderColor: HEALTH[hubHealth].color }} />}
              <div
                style={{
                  position: 'relative',
                  width: 46,
                  height: 46,
                  borderRadius: '13px',
                  background: `linear-gradient(160deg, ${C.lime}, #a9cc2c)`,
                  color: C.teal,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // all beacons paused → the hub goes quiet too
                  boxShadow: hubHealth === 'paused' ? 'none' : '0 6px 18px rgba(200,233,75,0.35)',
                  opacity: hubHealth === 'paused' ? 0.55 : 1,
                  filter: hubHealth === 'paused' ? 'grayscale(0.6)' : 'none',
                  transition: 'opacity .3s, filter .3s',
                }}
              >
                <Radar size={22} strokeWidth={2.2} />
              </div>
            </div>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <div style={{ fontSize: '1.05rem', fontWeight: 800, letterSpacing: '-0.2px' }}>Beacon Hub</div>
              <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.65)', marginTop: '2px' }}>
                {beacons.length === 0
                  ? 'No sheets are being watched yet'
                  : `Watching ${beacons.length} sheet${beacons.length === 1 ? '' : 's'} · last ping ${fmtAgo(summary?.last_ping_at, now)}`}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              {beacons.length > 0 && <Chip health={live > 0 ? 'live' : 'paused'}>{live} live</Chip>}
              {anyNew && <Chip health="new">{summary!.unseen_new_rows} new leads</Chip>}
              {failing > 0 && <Chip health="error">{failing} failing</Chip>}
              {paused > 0 && <Chip health="paused">{paused} paused</Chip>}
              <button
                type="button"
                onClick={onAdd}
                className="bct-lime"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  height: '32px',
                  padding: '0 14px',
                  borderRadius: '8px',
                  border: 'none',
                  background: C.lime,
                  color: C.teal,
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(200,233,75,0.35)',
                  transition: 'all 0.2s',
                }}
              >
                <Plus size={13} /> ADD BEACON
              </button>
            </div>
          </div>

          {/* Branches */}
          {loading && beacons.length === 0 ? (
            <div style={{ padding: '22px 0 8px 54px', fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: '7px' }}>
              <RefreshCw size={13} className="bct-spin" /> Loading beacons…
            </div>
          ) : beacons.length === 0 ? (
            <div className="bct-leaf bct-last">
              <span className="bct-trunk" style={{ background: 'repeating-linear-gradient(180deg, rgba(255,255,255,0.25) 0 4px, transparent 4px 8px)' }} />
              <span className="bct-branch" style={{ background: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.25) 0 4px, transparent 4px 8px)' }} />
              <div
                style={{
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px dashed rgba(255,255,255,0.25)',
                  fontSize: '0.74rem',
                  color: 'rgba(255,255,255,0.75)',
                  lineHeight: 1.6,
                }}
              >
                Add a beacon to any Google Sheet that collects leads (Meta lead ads, Google Forms, your team's sheet). It checks the
                sheet on a schedule and lights up here the moment new rows arrive.
              </div>
            </div>
          ) : (
            beacons.map((b, i) => (
              <Leaf key={b.id} b={b} last={i === beacons.length - 1} now={now} busy={busyIds.has(b.id)} props={props} />
            ))
          )}

          {/* Legend — a state animates only while at least one beacon is in it */}
          {beacons.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '12px', paddingLeft: '54px', fontSize: '0.6rem', color: 'rgba(255,255,255,0.55)' }}>
              {(['live', 'new', 'checking', 'error', 'paused'] as Health[]).map((k) => {
                const count = beacons.filter((b) => healthOf(b) === k).length;
                const active = count > 0;
                return (
                  <span
                    key={k}
                    title={active ? `${count} ${HEALTH[k].label.toLowerCase()}` : `None ${HEALTH[k].label.toLowerCase()}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      opacity: active ? 1 : 0.45,
                      color: active ? 'rgba(255,255,255,0.9)' : undefined,
                      fontWeight: active ? 700 : 400,
                      transition: 'opacity .3s',
                    }}
                  >
                    <BeaconDot health={k} size={7} still={!active} /> {HEALTH[k].label}
                    {active && <span style={{ fontVariantNumeric: 'tabular-nums' }}>· {count}</span>}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BeaconTree;
